/**
 * attendance.api.ts — Asistencia por rostro (dashboard).
 * Espejo de `aws-serverless/src/api/attendance.handler.ts`. Las llaves de S3 y los ids de rostro
 * nunca llegan al navegador: las fotos se piden con `fetchAttendancePhotoUrl` (URL firmada de 15 min).
 */
import { apiClient } from './client';

export type EmployeeStatus = 'ACTIVE' | 'PRE_TERMINATION' | 'INACTIVE';

export interface EmployeeRow {
  employeeId: string;
  name: string;
  employeeNumber?: string;
  crewId?: string;
  /** Usuario del sistema ligado (vacío o ausente = sin liga). */
  userId?: string;
  status: EmployeeStatus;
  missCount: number;
  enrollment: { status: 'NONE' | 'ENROLLED'; photos: number };
  consent: { acceptedAt: string; version: string } | null;
  biometricsDeletedAt?: string;
}

export interface ImportSummary {
  dryRun: boolean;
  created?: number;
  toCreate: number;
  alreadyExist: number;
  errors: { row: number; message: string }[];
  warnings: { row: number; message: string }[];
  preview: { row: number; name: string; employeeNumber?: string }[];
}

export interface DeletionReceipt {
  deletionId: string;
  employeeId: string;
  employeeName?: string;
  executedAt: string;
  facesDeleted: number;
  enrollPhotosDeleted: number;
  attendancePhotosDeleted: number;
}

export interface AttendanceSessionRow {
  sessionId: string;
  date: string;
  loteId: string;
  loteName?: string;
  crewNames?: string[];
  notes?: string;
  status: 'OPEN' | 'CLOSED';
  openedAt?: string;
  expectedEmployeeIds: string[];
}

export interface AttendanceSessionDetail {
  session: AttendanceSessionRow & { missingDecisions: Record<string, { decision: string; reason?: string }> };
  roster: { presentEmployeeIds: string[]; extras: { recordId: string; name: string }[]; missingEmployeeIds: string[] };
  employees: Record<string, { employeeId: string; name: string }>;
  records: { recordId: string; employeeId?: string; extraName?: string; method: string; serverTs: string; hasPhoto: boolean }[];
}

// En un subdominio de empresa, `:tenantId` de la ruta es el código (`app`), no el id de la empresa.
// Por eso no se arma la URL con él: el interceptor de apiClient inyecta el tenantId real de la sesión.
const base = (_tenantId: string) => '/api';

export const fetchEmployees = async (tenantId: string): Promise<EmployeeRow[]> =>
  (await apiClient.get(`${base(tenantId)}/employees`)).data.employees;

export interface CrewRow { crewId: string; name: string }
export const fetchCrews = async (tenantId: string): Promise<CrewRow[]> =>
  (await apiClient.get(`${base(tenantId)}/attendance/crews`)).data.crews;
export const createCrew = async (tenantId: string, name: string): Promise<CrewRow> =>
  (await apiClient.post(`${base(tenantId)}/attendance/crews`, { name })).data;
export const deleteCrew = async (tenantId: string, crewId: string) =>
  (await apiClient.delete(`${base(tenantId)}/attendance/crews/${crewId}`)).data;
export const setEmployeeCrew = async (tenantId: string, employeeId: string, crewId: string) =>
  (await apiClient.post(`${base(tenantId)}/employees/${employeeId}/crew`, { crewId })).data;

export const setEmployeeUser = async (tenantId: string, employeeId: string, userId: string) =>
  (await apiClient.post(`${base(tenantId)}/employees/${employeeId}/user`, { userId })).data;

export const createEmployee = async (tenantId: string, body: { name: string; employeeNumber?: string; crewId?: string }) =>
  (await apiClient.post(`${base(tenantId)}/employees`, body)).data;

export const decidePreBaja = async (tenantId: string, employeeId: string, decision: 'KEEP' | 'TERMINATE', reason: string) =>
  (await apiClient.post(`${base(tenantId)}/employees/${employeeId}/prebaja`, { decision, reason })).data;

export const deleteBiometrics = async (tenantId: string, employeeId: string, confirmName: string, reason: string): Promise<DeletionReceipt> =>
  (await apiClient.post(`${base(tenantId)}/employees/${employeeId}/delete-biometrics`, { confirmName, reason })).data.receipt;

export const updateEmployee = async (tenantId: string, employeeId: string, body: { name: string; employeeNumber: string; crewId: string }) =>
  (await apiClient.post(`${base(tenantId)}/employees/${employeeId}/update`, body)).data;

export const deleteEmployee = async (tenantId: string, employeeId: string, confirmName: string, reason: string) =>
  (await apiClient.post(`${base(tenantId)}/employees/${employeeId}/delete-employee`, { confirmName, reason })).data;

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export async function importEmployees(tenantId: string, file: File, dryRun: boolean): Promise<ImportSummary> {
  const fileBase64 = await fileToBase64(file);
  return (await apiClient.post(`${base(tenantId)}/employees/import`, { fileBase64, dryRun })).data;
}

export const fetchAttendanceSessions = async (tenantId: string, date?: string): Promise<AttendanceSessionRow[]> =>
  (await apiClient.get(`${base(tenantId)}/attendance/sessions`, { params: date ? { date } : {} })).data.sessions;

export const fetchAttendanceSession = async (tenantId: string, sessionId: string): Promise<AttendanceSessionDetail> =>
  (await apiClient.get(`${base(tenantId)}/attendance/sessions/${sessionId}`)).data;

export const fetchAttendancePhotoUrl = async (tenantId: string, sessionId: string, recordId: string): Promise<string> =>
  (await apiClient.get(`${base(tenantId)}/attendance/photo-url`, { params: { sessionId, recordId } })).data.url;

/** Fotos de enrolamiento de una persona (URLs firmadas de 15 min). */
export const fetchEnrollPhotos = async (tenantId: string, employeeId: string): Promise<{ url: string }[]> =>
  (await apiClient.get(`${base(tenantId)}/employees/${employeeId}/enroll-photos`)).data.photos;

export interface SessionPhoto { recordId: string; names: string[]; url: string }
/** Fotos tomadas para la lista del día (una por foto, con los nombres de quienes salen en ella). */
export const fetchSessionPhotos = async (tenantId: string, sessionId: string): Promise<SessionPhoto[]> =>
  (await apiClient.get(`${base(tenantId)}/attendance/sessions/${sessionId}/photos`)).data.photos;

/** Foto con sello visible (nombre, hora, ubicación y lugar), lista para abrir como imagen. */
/** `all`: enmarca a todas las personas de la foto (miniatura de una foto de grupo). */
export async function fetchAttendanceStampedPhoto(tenantId: string, sessionId: string, recordId: string, all = false): Promise<Blob> {
  const { imageBase64, contentType } = (await apiClient.get(`${base(tenantId)}/attendance/stamped-photo`, { params: { sessionId, recordId, ...(all ? { all: 1 } : {}) } })).data;
  const bytes = Uint8Array.from(atob(imageBase64), (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: contentType });
}

export interface ReportFilters { from: string; to: string; loteId?: string; crewId?: string }
export interface ReportPerson {
  employeeId: string; name: string; employeeNumber?: string; crewId?: string; status: EmployeeStatus;
  present: number; absent: number; justified: number; undecided: number; pct: number | null;
  /** Retardos y minutos trabajados (entrada = primera foto del día, salida = última). */
  late: number; workedMinutes: number; daysWithHours: number;
}
export interface ReportDay {
  date: string; sessionId: string; loteId: string; notes?: string; status: 'OPEN' | 'CLOSED';
  expected: number; present: number; absent: number; justified: number; undecided: number; extras: number;
}
export interface AttendanceReport {
  filters: ReportFilters;
  persons: ReportPerson[];
  days: ReportDay[];
  extras: { date: string; loteId: string; name: string }[];
  totals: { sessions: number; present: number; absent: number; justified: number; undecided: number; extras: number; pct: number | null };
}

export const fetchAttendanceReport = async (tenantId: string, f: ReportFilters): Promise<AttendanceReport> =>
  (await apiClient.get(`${base(tenantId)}/attendance/report`, { params: f })).data;

/** Excel del reporte: el servidor lo devuelve en base64 dentro de JSON. */
export async function fetchAttendanceReportXlsx(tenantId: string, f: ReportFilters): Promise<{ filename: string; blob: Blob }> {
  const { filename, fileBase64 } = (await apiClient.get(`${base(tenantId)}/attendance/report`, { params: { ...f, format: 'xlsx' } })).data;
  const bytes = Uint8Array.from(atob(fileBase64), (c) => c.charCodeAt(0));
  return { filename, blob: new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }) };
}
